import React from "react";
import { Link } from "react-router-dom";
import EventFlyer from "./EventFlyer";
import { normalizeExternalUrl } from "../urlUtils";
import { eventDetailPath } from "../eventRoutes";
import {
  formatEventDateLabel,
  formatEventTimeLabel,
  getEventCostLabel,
  normalizeEventRecord,
} from "../eventViewModel";

export default function EventCard({ event, variant = "standard", flyerEager = false }) {
  const item = normalizeEventRecord(event);
  const compact = variant === "compact";
  const featured = variant === "featured";
  const timeLabel = formatEventTimeLabel(item);
  const costLabel = getEventCostLabel(item);
  const websiteUrl = normalizeExternalUrl(item.website);
  const bookingUrl = normalizeExternalUrl(item.bookingUrl);
  const detailPath = eventDetailPath(item);
  const locationLabel = [item.venueName, item.address, item.postcode]
    .filter(Boolean)
    .join(", ");

  return (
    <article
      data-content-type="event"
      data-event-variant={variant}
      className={`min-w-0 overflow-hidden rounded-2xl border border-white/10 bg-white/5 ${
        featured ? "p-5 md:p-6" : "p-4"
      }`}
    >
      <EventFlyer
        event={item}
        compact={compact}
        eager={flyerEager}
        className="mb-4"
      />

      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#F4A261]">
            {item.category || "Community event"}
          </p>
          <h3 className={`${featured ? "text-xl" : "text-lg"} mt-1 break-words font-semibold`}>
            {item.title}
          </h3>
        </div>
        <span className="shrink-0 rounded-full border border-white/10 bg-white/10 px-2 py-1 text-xs">
          Event
        </span>
      </div>

      {item.description ? (
        <p className={`mt-2 break-words text-sm text-white/70 ${compact ? "line-clamp-3" : ""}`}>
          {item.description}
        </p>
      ) : null}

      <dl className="mt-4 grid gap-2 text-sm text-white/80 sm:grid-cols-2">
        <div>
          <dt className="text-xs uppercase tracking-wide text-white/50">Date</dt>
          <dd>{formatEventDateLabel(item)}</dd>
        </div>
        {timeLabel ? (
          <div>
            <dt className="text-xs uppercase tracking-wide text-white/50">Time</dt>
            <dd>{timeLabel}</dd>
          </div>
        ) : null}
        {locationLabel ? (
          <div>
            <dt className="text-xs uppercase tracking-wide text-white/50">Location</dt>
            <dd className="break-words">{locationLabel}</dd>
          </div>
        ) : null}
        {costLabel ? (
          <div>
            <dt className="text-xs uppercase tracking-wide text-white/50">Cost</dt>
            <dd>{costLabel}</dd>
          </div>
        ) : null}
      </dl>

      {(detailPath || bookingUrl || websiteUrl) && (
        <div className="mt-4 flex flex-wrap gap-2">
          {detailPath ? (
            <Link
              to={detailPath}
              className="inline-flex items-center justify-center rounded-xl bg-[#E63946] px-3 py-2 text-sm font-semibold hover:opacity-90"
            >
              View event details
            </Link>
          ) : null}
          {bookingUrl ? (
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-sm font-semibold hover:bg-white/15"
            >
              Book or register
            </a>
          ) : null}
          {websiteUrl ? (
            <a
              href={websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-sm hover:bg-white/15"
            >
              Event website
            </a>
          ) : null}
        </div>
      )}

      {(item.organiserName || item.contact) && (
        <p className="mt-4 break-words text-xs text-white/55">
          {item.organiserName ? `Organised by ${item.organiserName}` : ""}
          {item.organiserName && item.contact ? " · " : ""}
          {item.contact ? `Contact: ${item.contact}` : ""}
        </p>
      )}
    </article>
  );
}
